/**
 * Cookie Consent Utility Functions
 * Handles cookie consent storage and retrieval
 */

import * as Sentry from '@sentry/react';

const { logger } = Sentry;
const COOKIE_CONSENT_KEY = 'cookie_consent'

interface CookieConsent {
  accepted: boolean
  timestamp: string
  expiry: string
}

/**
 * Get the current cookie consent status
 * @returns Consent object with accepted, timestamp, and expiry, or null if not set
 */
export function getCookieConsent(): CookieConsent | null {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!consent) {
      return null
    }

    const consentData = JSON.parse(consent) as CookieConsent
    
    // Check if consent has expired
    if (consentData.expiry) {
      const expiryDate = new Date(consentData.expiry)
      if (expiryDate < new Date()) {
        // Consent expired, remove it
        localStorage.removeItem(COOKIE_CONSENT_KEY)
        return null
      }
    }

    return consentData
  } catch (error) {
    logger.error('Error reading cookie consent', {
      error: error instanceof Error ? error.message : String(error),
    });
    return null
  }
}

/**
 * Check if user has given consent
 * @returns True if user has accepted cookies
 */
export function hasCookieConsent(): boolean {
  const consent = getCookieConsent()
  return consent !== null && consent.accepted === true
}

/**
 * Set cookie consent
 * @param accepted - Whether user accepted cookies
 * @param expiryDays - Number of days until consent expires (default: 365)
 */
export function setCookieConsent(accepted: boolean, expiryDays: number = 365): void {
  const consentData: CookieConsent = {
    accepted,
    timestamp: new Date().toISOString(),
    expiry: new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toISOString()
  }
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData))
}

/**
 * Clear cookie consent (useful for testing or allowing users to change preferences)
 */
export function clearCookieConsent(): void {
  localStorage.removeItem(COOKIE_CONSENT_KEY)
}
